import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { copyToClipboard } from '../lib/utils';
import WebsiteAnalysisSection from '../components/WebsiteAnalysisSection';
import PersonasDisplay from '../components/PersonasDisplay';
import ScrapedContentDisplay from '../components/ScrapedContentDisplay';
import OpportunitiesTab from '../components/OpportunitiesTab';
import { 
  ArrowLeft,
  Globe,
  Shield,
  BookOpen,
  Loader2,
  CheckCircle2,
  RefreshCw,
  Save,
  Plus,
  ExternalLink,
  Layers,
  Package,
  Target
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AssetWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scrapedContent, setScrapedContent] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [policy, setPolicy] = useState('');
  const [knowledgeBase, setKnowledgeBase] = useState('');
  const [saving, setSaving] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const [editingAnalysis, setEditingAnalysis] = useState(false);
  const [editedContext, setEditedContext] = useState(null);
  const [editedPersonas, setEditedPersonas] = useState(null);

  const fetchAsset = useCallback(async () => {
    try {
      const [assetRes, contentRes, campaignsRes] = await Promise.all([
        axios.get(`${API}/assets/${id}`),
        axios.get(`${API}/assets/${id}/content`),
        axios.get(`${API}/assets/${id}/campaigns`)
      ]);
      setAsset(assetRes.data);
      setPolicy(assetRes.data.policy || '');
      setKnowledgeBase(assetRes.data.knowledge_base || '');
      setScrapedContent(contentRes.data);
      setCampaigns(campaignsRes.data);
      if (assetRes.data.website_context) {
        setEditedContext(JSON.parse(assetRes.data.website_context));
      }
      if (assetRes.data.personas) {
        try {
          setEditedPersonas(JSON.parse(assetRes.data.personas));
        } catch (e) {
          setEditedPersonas(null);
        }
      }
    } catch (error) {
      toast.error('Failed to load asset');
      navigate('/assets');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchAsset();
  }, [fetchAsset]);

  const handleScrape = useCallback(async () => {
    setScraping(true);
    try {
      await axios.post(`${API}/assets/${id}/scrape`);
      toast.success('Website scanned successfully!');
      fetchAsset();
    } catch (error) {
      toast.error('Failed to scan website');
    } finally {
      setScraping(false);
    }
  }, [id, fetchAsset]);

  // Auto-scrape if asset is in draft status
  useEffect(() => {
    if (asset && asset.status === 'draft') {
      handleScrape();
    }
  }, [asset?.status, handleScrape]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = { policy, knowledge_base: knowledgeBase };
      if (editedContext) {
        updateData.website_context = JSON.stringify(editedContext);
      }
      if (editedPersonas) {
        updateData.personas = JSON.stringify(editedPersonas);
      }
      await axios.put(`${API}/assets/${id}`, updateData);
      toast.success('Asset saved successfully!');
      setEditingAnalysis(false);
      fetchAsset();
    } catch (error) {
      toast.error('Failed to save asset');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (asset?.website_context) {
      setEditedContext(JSON.parse(asset.website_context));
    }
    if (asset?.personas) {
      try {
        setEditedPersonas(JSON.parse(asset.personas));
      } catch (e) {
        setEditedPersonas(null);
      }
    }
    setEditingAnalysis(false);
  };

  const handlePersonasUpdate = (updatedPersonas) => {
    setEditedPersonas(updatedPersonas);
  };

  const handleCreateCampaign = () => {
    navigate(`/dashboard?create=true&asset_id=${id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!asset) return null;

  const websiteContext = asset.website_context ? JSON.parse(asset.website_context) : null;
  const displayPersonas = editedPersonas || (asset.personas ? JSON.parse(asset.personas) : null);

  const getStatusBadge = () => {
    if (asset.status === 'ready') {
      return (
        <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700">
          <CheckCircle2 className="w-3.5 h-3.5" /> Ready
        </span>
      );
    }
    if (asset.status === 'scraping') {
      return (
        <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-blue-100 text-blue-700">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Scanning...
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
        Draft
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-surface/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/assets')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-5 h-5" />
              Back to Assets
            </button>
            <span className="text-muted-foreground">/</span>
            <div className="flex flex-col gap-1">
              {asset.asset_slug && (
                <code 
                  className="text-xs font-mono bg-muted/50 text-muted-foreground px-2 py-0.5 rounded cursor-pointer hover:bg-muted transition-colors w-fit"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const success = await copyToClipboard(asset.asset_slug);
                    if (success) {
                      toast.success('Asset ID copied to clipboard');
                    }
                  }}
                  title="Click to copy Asset ID"
                  data-testid="asset-slug"
                >
                  {asset.asset_slug}
                </code>
              )}
              <h1 className="text-xl font-semibold text-foreground">{asset.name}</h1>
            </div>
            {getStatusBadge()}
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={handleSave} disabled={saving} variant="outline" className="rounded-full" data-testid="save-asset-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
            <Button onClick={handleCreateCampaign} className="bg-primary text-white hover:bg-primary/90 rounded-full" data-testid="create-campaign-from-asset">
              <Plus className="w-4 h-4 mr-2" />
              Create Campaign
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Website URL Card */}
        <div className="bg-surface border border-border rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Globe className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">Website</h2>
                <a href={asset.url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1">
                  {asset.url}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
            <Button onClick={handleScrape} disabled={scraping} variant="outline" size="sm" className="rounded-full" data-testid="rescan-btn">
              {scraping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Re-scan
            </Button>
          </div>
        </div>

        {/* Website Analysis */}
        <WebsiteAnalysisSection
          websiteContext={websiteContext}
          editedContext={editedContext}
          setEditedContext={setEditedContext}
          editingAnalysis={editingAnalysis}
          setEditingAnalysis={setEditingAnalysis}
          handleSave={handleSave}
          handleCancelEdit={handleCancelEdit}
          saving={saving}
        />

        {/* Customer Personas */}
        <PersonasDisplay 
          personas={displayPersonas} 
          onUpdate={handlePersonasUpdate}
          saving={saving}
        />

        {/* Opportunities Card Section - Hidden */}
        <div className="hidden" data-testid="opportunities-section">
          <OpportunitiesTab assetId={id} />
        </div>

        {/* Tabs Section */}
        <div className="bg-surface border border-border rounded-2xl overflow-hidden">
          <div className="flex border-b border-border overflow-x-auto">
            <TabButton 
              active={activeTab === 'content'} 
              onClick={() => setActiveTab('content')}
              icon={Layers}
              label="Scraped Content"
            />
            <TabButton 
              active={activeTab === 'policy'} 
              onClick={() => setActiveTab('policy')}
              icon={Shield}
              label="Messaging Policy"
            />
            <TabButton 
              active={activeTab === 'knowledge'} 
              onClick={() => setActiveTab('knowledge')}
              icon={BookOpen}
              label="Knowledge Base"
            />
          </div>
          <div className="p-6">
            {activeTab === 'policy' && (
              <Textarea
                value={policy}
                onChange={(e) => setPolicy(e.target.value)}
                placeholder="Enter company messaging policies, tone guidelines, claims to avoid, compliance rules, etc."
                className="min-h-[300px] font-mono text-sm"
                data-testid="policy-textarea"
              />
            )}
            {activeTab === 'knowledge' && (
              <Textarea
                value={knowledgeBase}
                onChange={(e) => setKnowledgeBase(e.target.value)}
                placeholder="Add any additional company information that should inform AI-generated content."
                className="min-h-[300px] font-mono text-sm"
                data-testid="knowledge-base-textarea"
              />
            )}
            {activeTab === 'content' && (
              <ScrapedContentDisplay 
                content={scrapedContent} 
                onScrape={handleScrape} 
                scraping={scraping} 
                pageStructure={websiteContext?.page_structure}
              />
            )}
          </div>
        </div>

        {/* Connected Campaigns */}
        {campaigns.length > 0 && (
          <div className="bg-surface border border-border rounded-2xl p-6 mt-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Campaigns Using This Asset</h3>
                <p className="text-sm text-muted-foreground">{campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {campaigns.map((campaign) => (
                <CampaignCard key={campaign.id} campaign={campaign} onClick={() => navigate(`/campaign/${campaign.id}`)} />
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// Simple sub-components to avoid babel issues
function TabButton({ active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex items-center justify-center gap-2 px-4 py-4 text-sm font-medium transition-colors ${
        active
          ? 'bg-primary/5 text-primary border-b-2 border-primary -mb-[1px]'
          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

function CampaignCard({ campaign, onClick }) {
  return (
    <button
      onClick={onClick}
      className="p-4 bg-muted/30 hover:bg-muted/50 rounded-xl text-left transition-colors flex items-center justify-between group border border-border/50"
    >
      <div>
        <p className="font-medium text-foreground group-hover:text-primary transition-colors">{campaign.name}</p>
        <p className="text-xs text-muted-foreground">{campaign.status} • {new Date(campaign.created_at).toLocaleDateString()}</p>
      </div>
      <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary opacity-0 group-hover:opacity-100 transition-all" />
    </button>
  );
}
